// Avatar Validation
// ------------------
// Defense-in-depth client-side validation for avatar uploads. The backend
// remains the authoritative validator; this exists to catch obvious abuse
// before we burn a presigned-URL request and an S3 PUT on something we
// already know we'll reject.
//
// Why magic bytes and not just the MIME / extension?
// ---------------------------------------------------
// The browser populates `File.type` from the OS file-extension mapping
// (Windows associations, macOS UTIs, Linux mimedb). It is **not** read
// from the file's content. An attacker who renames `payload.svg` →
// `payload.png` will get `image/png` in `File.type` despite the bytes
// being SVG. Allowing that file through into the avatar pipeline means
// an `<img src=avatar.svg>` later in the product can execute embedded
// `<script>` against the user's own origin (XSS via avatar).
//
// Magic-byte sniffing reads the actual leading bytes of the file and
// matches them against the well-known signatures for the formats we
// allow. SVG is XML text and starts with `<` (or BOM + `<` or whitespace
// + `<`); we explicitly reject anything that looks like XML.
//
// Why reject SVG entirely instead of sanitising?
// ----------------------------------------------
// Avatars don't need vector graphics. The few hundred KB saved by SVG
// over a well-compressed PNG is not worth running an XML sanitiser
// (DOMPurify et al.) over user-uploaded content on every render.
// Sanitising SVG safely is a known-hard problem (mXSS, foreignObject,
// CSS `behavior:`, attribute-based scripts, namespace tricks) and the
// surface area changes with every browser release. Allowlisting raster
// formats removes the entire class of bug.
//
// The backend should mirror this rejection (and re-validate magic bytes
// from the S3 object before serving it back). Don't trust this alone.

const MEGABYTE = 1024 * 1024;
export const MAX_AVATAR_FILE_SIZE_BYTES = 5 * MEGABYTE;

// Allowed raster image MIME types. Server should re-check after upload.
export const ALLOWED_AVATAR_MIME_TYPES: readonly string[] = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
];

// Number of leading bytes we need to identify any of the allowed formats.
// PNG/GIF/JPEG: 8 bytes is enough. WebP needs 12 (RIFF + size + WEBP).
const MAGIC_BYTE_PREFIX_LEN = 12;

// Hex string -> match function. We test each signature against a hex
// string of the file's first MAGIC_BYTE_PREFIX_LEN bytes; using hex
// instead of byte-array comparison keeps the rules readable and avoids
// per-call typed-array fiddling.
//
// Signatures sourced from the file-format specs:
//   PNG  — 89 50 4E 47 0D 0A 1A 0A
//   JPEG — FF D8 FF (followed by E0/E1/E2/E3/E8/EE/DB...; we match the
//          three-byte SOI+marker prefix)
//   GIF  — 47 49 46 38 (37|39) 61   — "GIF87a" or "GIF89a"
//   WebP — 52 49 46 46 ?? ?? ?? ?? 57 45 42 50 — "RIFF"...."WEBP"
//
// Anything else (including "<svg", "<?xml", "<!DOCTYPE", BOM-prefixed
// XML, plain text, ZIP, PDF, ELF, MZ, …) is rejected.
function matchesAllowedMagic(hexLeadingBytes: string): boolean {
  // PNG
  if (hexLeadingBytes.startsWith("89504e470d0a1a0a")) return true;
  // JPEG (any APPn / DQT / SOF0 marker)
  if (hexLeadingBytes.startsWith("ffd8ff")) return true;
  // GIF87a / GIF89a
  if (
    hexLeadingBytes.startsWith("474946383761") ||
    hexLeadingBytes.startsWith("474946383961")
  )
    return true;
  // WebP: RIFF????WEBP
  if (
    hexLeadingBytes.startsWith("52494646") &&
    hexLeadingBytes.substring(16, 24) === "57454250"
  )
    return true;
  return false;
}

export interface AvatarValidationResult {
  ok: boolean;
  // Operator-friendly reason for the rejection. Suitable for surfacing
  // verbatim to the end user — no PII, no internals.
  errorMessage?: string;
}

// Validate a File chosen via <input type="file">.
//
// Order of checks matters:
//   1. Size  — cheapest, no I/O.
//   2. MIME  — cheap, but spoofable.
//   3. Magic — definitive.
//
// On any rejection we short-circuit so we never call slice().arrayBuffer()
// on an obviously bad file (a 2GB upload would otherwise tie up the main
// thread reading the head before the size check kicked in).
export async function validateAvatarFile(
  fileToValidate: File
): Promise<AvatarValidationResult> {
  if (fileToValidate.size === 0) {
    return { ok: false, errorMessage: "Selected file is empty" };
  }
  if (fileToValidate.size > MAX_AVATAR_FILE_SIZE_BYTES) {
    return { ok: false, errorMessage: "Image must be under 5 MB" };
  }

  // MIME allowlist — fast reject for obvious mismatches like image/svg+xml,
  // application/pdf, text/html, etc. Browsers can occasionally report an
  // empty `type` for files dragged from non-Finder sources; we don't
  // accept empty and rely on the magic-bytes check for the final verdict.
  if (!ALLOWED_AVATAR_MIME_TYPES.includes(fileToValidate.type)) {
    return {
      ok: false,
      errorMessage: "Please select a PNG, JPEG, GIF or WebP image",
    };
  }

  // Magic-byte sniff. Reads only the head of the file — no full-file load.
  let leadingHexString: string;
  try {
    const headBuffer = await fileToValidate
      .slice(0, MAGIC_BYTE_PREFIX_LEN)
      .arrayBuffer();
    const headByteArray = new Uint8Array(headBuffer);
    leadingHexString = Array.from(headByteArray)
      .map((singleByte) => singleByte.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return {
      ok: false,
      errorMessage: "Unable to read the selected file",
    };
  }

  if (!matchesAllowedMagic(leadingHexString)) {
    // Generic phrasing — we don't tell the user *what* we detected because
    // the file could be anything (SVG, HTML, ZIP, executable). The
    // remediation is the same: pick a real image.
    return {
      ok: false,
      errorMessage:
        "That file does not appear to be a valid PNG, JPEG, GIF or WebP image",
    };
  }

  return { ok: true };
}
